/**
 * Pending Absence (HR) — organisation-wide list for authorized HR (web only).
 * SRS: HR sees and resolves the pending absences of all staff (exempt staff excluded) with advanced
 * filters and Select All. Actions: HR Resolved Presence (with reason), Authorized Absence,
 * Unauthorized Absence. HR is never limited by the deadline. The same data feeds the Excel report.
 */
import React, { useCallback, useEffect, useState } from "react";
import Select from "react-select";
import * as XLSX from "xlsx";
import { ToastContainer, toast } from "react-toastify";
import { pendingAbsenceApi } from "../../utils/pendingAbsenceApi";
import LoadingIndicator from "../Common/components/LoadingIndicator";
import {
  ActionDialog,
  DateInput,
  DeadlineLabel,
  NoPermission,
  PageHeading,
  Pager,
  ShareButton,
  StatusBadge,
  usePagePermission,
} from "./PendingAbsenceCommon";
import {
  STATUS_META,
  buildCascadeOptions,
  buildReportRows,
  canHrAct,
  describeLogAction,
  describeStatusCode,
  formatDate,
  formatTime,
  isAllSelected,
  reconcileFilters,
  uniqueEmployees,
  itemKey,
  summarizeResults,
  toRequestItems,
  toggleAll,
  toggleKey,
} from "./pendingAbsenceHelpers";

const PERMISSION_TITLE = "Pending Absence HR";
const PAGE_SIZE = 50;

const statusOptions = Object.keys(STATUS_META).map((value) => ({
  value,
  label: STATUS_META[value].label,
}));

const EMPTY_FILTERS = {
  from: "",
  to: "",
  region: { value: 0, label: "All" },
  branch: { value: 0, label: "All" },
  department: { value: 0, label: "All" },
  designation: { value: 0, label: "All" },
  employee: { value: 0, label: "All" },
  statuses: [],
  overdueOnly: false,
  includeResolved: false,
};

/** Query parameters for the list / report endpoints from the filter state. */
function toQuery(filters) {
  const id = (option) => (option && option.value ? option.value : undefined);
  return {
    from: filters.from,
    to: filters.to,
    region_id: id(filters.region),
    branch_id: id(filters.branch),
    department_id: id(filters.department),
    designation_id: id(filters.designation),
    user_id: id(filters.employee),
    status: filters.statuses.length > 0 ? filters.statuses.map((s) => s.value).join(",") : undefined,
    overdue_only: filters.overdueOnly ? 1 : undefined,
    include_resolved: filters.includeResolved ? 1 : undefined,
  };
}

export default function PendingAbsenceHR() {
  const permission = usePagePermission(PERMISSION_TITLE);

  const [employees, setEmployees] = useState([]); // the people the filter dropdowns are built from
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [result, setResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [dialog, setDialog] = useState(null); // 'resolve-presence' | 'authorized' | 'unauthorized'
  const [form, setForm] = useState({ reason: "" });
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(null); // { item, rows }
  const [balance, setBalance] = useState(null); // { item, rows }
  const [exporting, setExporting] = useState(false);

  /* ----------------------------- filter options ---------------------------- */

  useEffect(() => {
    if (!permission.loaded || !permission.isView) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await pendingAbsenceApi.get("/hr/filter-options");
        if (!cancelled) setEmployees(uniqueEmployees(data && data.employees));
      } catch (error) {
        if (!cancelled) toast.error(`Could not load the filter lists. ${error.message}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [permission.loaded, permission.isView]);

  // Region -> Branch and Department -> Designation; Employee follows all of them.
  const options = buildCascadeOptions(employees, filters);

  /* --------------------------------- search -------------------------------- */

  const search = useCallback(
    async (pageToLoad = 1) => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await pendingAbsenceApi.get("/hr", {
          ...toQuery(filters),
          page: pageToLoad,
          page_size: PAGE_SIZE,
        });
        setResult(data);
        setPage(pageToLoad);
        setSelected(new Set());
      } catch (error) {
        setLoadError(error.message);
      } finally {
        setLoading(false);
      }
    },
    [filters]
  );

  // First load once the permission is known.
  useEffect(() => {
    if (permission.loaded && permission.isView) search(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission.loaded, permission.isView]);

  const items = (result && result.items) || [];
  const selectedItems = toRequestItems(items, selected);
  // With one day selected, HR sees the employee's own reason (if they sent a correction).
  const selectedRows = items.filter((item) => selected.has(itemKey(item)));
  const employeeReason = selectedRows.length === 1 && selectedRows[0].request ? selectedRows[0].request.reason : null;

  /* --------------------------------- actions -------------------------------- */

  const openDialog = (kind) => {
    setForm({ reason: "" });
    setDialog(kind);
  };

  // Only HR Resolved Presence needs a reason. The attendance times are filled in by the server.
  const dialogError = () =>
    dialog === "resolve-presence" && !form.reason.trim() ? "Please enter a reason." : null;

  const submitDialog = async () => {
    const error = dialogError();
    if (error) return toast.error(error);

    const body = { items: selectedItems, reason: form.reason.trim() };

    setBusy(true);
    try {
      const data = await pendingAbsenceApi.post(`/hr/${dialog}`, body);
      const summary = summarizeResults(data);
      if (summary.succeeded > 0) {
        toast.success(`${summary.succeeded} record${summary.succeeded === 1 ? "" : "s"} updated.`);
      }
      summary.failures.forEach((message) => toast.error(message, { autoClose: 9000 }));
      setDialog(null);
      await search(page);
    } catch (requestError) {
      toast.error(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  const showHistory = async (item) => {
    try {
      const rows = await pendingAbsenceApi.get("/hr/history", {
        user_id: item.user_id,
        absence_date: item.absence_date,
      });
      setHistory({ item, rows });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const showBalance = async (item) => {
    try {
      const rows = await pendingAbsenceApi.get(`/hr/leave-balance/${item.user_id}`);
      setBalance({ item, rows: rows || [] });
    } catch (error) {
      toast.error(error.message);
    }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      let rows = [];
      let pageToLoad = 1;
      let total = 0;
      do {
        // eslint-disable-next-line no-await-in-loop
        const data = await pendingAbsenceApi.get("/hr/report", {
          ...toQuery(filters),
          page: pageToLoad,
          page_size: 5000,
        });
        rows = rows.concat(data.items);
        total = data.total;
        pageToLoad += 1;
      } while (rows.length < total);

      if (rows.length === 0) return toast.info("There is nothing to export.");

      const sheet = XLSX.utils.json_to_sheet(buildReportRows(rows));
      const book = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(book, sheet, "Pending Absence");
      XLSX.writeFile(book, `pending_absence_report_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setExporting(false);
    }
  };

  /* ---------------------------------- view --------------------------------- */

  if (!permission.loaded) return <LoadingIndicator marginTop={35} />;
  if (!permission.isView) return <NoPermission />;

  const canEdit = Boolean(permission.isEdit);
  const setFilter = (key) => (value) => setFilters(reconcileFilters({ ...filters, [key]: value }, employees));

  return (
    <div>
      <ToastContainer position={toast.POSITION.TOP_RIGHT} />
      <PageHeading title="Pending Absence (HR)" />
      <div className="wrapper wrapper-content animated fadeInRight pending-absence-page">
        <div className="ibox float-e-margins pa-padded-card">
          <div className="ibox-content">
            <div className="row">
              <div className="col-sm-2 form-group">
                <label>From</label>
                <DateInput value={filters.from} onChange={(value) => setFilters({ ...filters, from: value })} />
              </div>
              <div className="col-sm-2 form-group">
                <label>To</label>
                <DateInput value={filters.to} onChange={(value) => setFilters({ ...filters, to: value })} />
              </div>
              <div className="col-sm-2 form-group">
                <label>Region</label>
                <Select value={filters.region} options={options.region} onChange={setFilter("region")} />
              </div>
              <div className="col-sm-3 form-group">
                <label>Branch</label>
                <Select value={filters.branch} options={options.branch} onChange={setFilter("branch")} />
              </div>
              <div className="col-sm-3 form-group">
                <label>Department</label>
                <Select
                  value={filters.department}
                  options={options.department}
                  onChange={setFilter("department")}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-sm-3 form-group">
                <label>Designation</label>
                <Select
                  value={filters.designation}
                  options={options.designation}
                  onChange={setFilter("designation")}
                />
              </div>
              <div className="col-sm-3 form-group">
                <label>Employee</label>
                <Select
                  value={filters.employee}
                  options={options.employee}
                  onChange={setFilter("employee")}
                />
              </div>
              <div className="col-sm-3 form-group">
                <label>Status (empty = all open and classified)</label>
                <Select
                  isMulti
                  value={filters.statuses}
                  options={statusOptions}
                  onChange={(value) => setFilters({ ...filters, statuses: value || [] })}
                />
              </div>
              <div className="col-sm-3 form-group pa-filter-option">
                <label style={{ marginRight: 12, fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={filters.overdueOnly}
                    onChange={(event) => setFilters({ ...filters, overdueOnly: event.target.checked })}
                  />{" "}
                  Overdue only
                </label>
                <label style={{ fontWeight: 400 }}>
                  <input
                    type="checkbox"
                    checked={filters.includeResolved}
                    onChange={(event) =>
                      setFilters({ ...filters, includeResolved: event.target.checked })
                    }
                  />{" "}
                  Include resolved
                </label>
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-primary"
                style={{ marginRight: 8 }}
                disabled={loading}
                onClick={() => search(1)}
              >
                Search
              </button>
              <button
                type="button"
                className="btn btn-white"
                style={{ marginRight: 8 }}
                disabled={loading}
                onClick={() => setFilters(EMPTY_FILTERS)}
              >
                Reset
              </button>
              <button
                type="button"
                className="btn btn-default"
                disabled={exporting}
                onClick={exportExcel}
              >
                {exporting ? "Exporting..." : "Export to Excel"}
              </button>
            </div>
          </div>
        </div>

        <div className="ibox float-e-margins">
          <div className="ibox-title" style={{ display: "flex", alignItems: "center" }}>
            <h5 style={{ flex: 1 }}>
              Pending absences
              {result && result.total !== undefined ? ` (${result.total})` : ""}
            </h5>
            {canEdit && (
              <div>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  style={{ marginRight: 6 }}
                  disabled={selectedItems.length === 0}
                  onClick={() => openDialog("resolve-presence")}
                >
                  HR Resolved Presence ({selectedItems.length})
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  style={{ marginRight: 6 }}
                  disabled={selectedItems.length === 0}
                  onClick={() => openDialog("authorized")}
                >
                  Authorized Absence
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  disabled={selectedItems.length === 0}
                  onClick={() => openDialog("unauthorized")}
                >
                  Unauthorized Absence
                </button>
              </div>
            )}
          </div>
          <div className="ibox-content">
            {loadError && <div className="alert alert-danger">{loadError}</div>}
            {loading && <LoadingIndicator backgroundColor="#ffffff" />}
            {!loading && result && !result.live && (
              <div className="alert alert-info">
                Pending Absence has not started yet. Records appear after the go-live date.
              </div>
            )}
            {!loading && result && result.live && items.length === 0 && (
              <p className="text-center text-muted" style={{ margin: 20 }}>
                No records match the filters.
              </p>
            )}

            {!loading && items.length > 0 && (
              <>
                <div className="table-responsive">
                  <table className="table table-striped table-bordered table-hover">
                    <thead>
                      <tr>
                        <th style={{ width: 36 }}>
                          <input
                            type="checkbox"
                            aria-label="Select all on this page"
                            checked={isAllSelected(items, selected, canHrAct)}
                            onChange={() => setSelected(toggleAll(items, selected, canHrAct))}
                          />
                        </th>
                        <th>Employee</th>
                        <th>Designation</th>
                        <th>Branch</th>
                        <th>Absence Date</th>
                        <th>Status</th>
                        <th>Employee's Correction</th>
                        <th>Comment</th>
                        <th>Resolved By</th>
                        <th>Deadline</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item) => (
                        <tr key={itemKey(item)}>
                          <td>
                            {canHrAct(item) && (
                              <input
                                type="checkbox"
                                aria-label={`Select ${item.display_name} ${item.absence_date}`}
                                checked={selected.has(itemKey(item))}
                                onChange={() => setSelected(toggleKey(selected, itemKey(item)))}
                              />
                            )}
                          </td>
                          <td>{item.display_name}</td>
                          <td>{item.designation}</td>
                          <td>{item.branch}</td>
                          <td style={{ whiteSpace: "nowrap" }}>{formatDate(item.absence_date)}</td>
                          <td>
                            <StatusBadge status={item.status} />
                          </td>
                          <td>
                            {item.request ? item.request.reason : ""}
                          </td>
                          <td>{item.comment}</td>
                          <td>
                            {item.resolved_by_name}
                            {item.resolved_date ? ` (${formatDate(item.resolved_date.slice(0, 10))})` : ""}
                          </td>
                          <td>
                            <DeadlineLabel item={item} />
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <button
                              type="button"
                              className="btn btn-white btn-xs"
                              style={{ marginRight: 4 }}
                              onClick={() => showHistory(item)}
                            >
                              History
                            </button>
                            <button
                              type="button"
                              className="btn btn-white btn-xs"
                              onClick={() => showBalance(item)}
                            >
                              Leave balance
                            </button>
                            <span style={{ marginLeft: 4 }}>
                              <ShareButton item={item} items={items} />
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pager page={page} pageSize={PAGE_SIZE} total={result.total} onChange={search} />
              </>
            )}
          </div>
        </div>
      </div>

      <ActionDialog
        visible={dialog !== null}
        title={
          dialog === "resolve-presence"
            ? "HR Resolved Presence"
            : dialog === "authorized"
            ? "Authorized Absence"
            : "Unauthorized Absence"
        }
        confirmLabel="Confirm"
        confirmClassName={
          dialog === "unauthorized" ? "btn btn-danger" : dialog === "authorized" ? "btn btn-success" : "btn btn-primary"
        }
        onClose={() => setDialog(null)}
        onConfirm={submitDialog}
        busy={busy}
      >
        <p className="text-muted">
          {selectedItems.length} record{selectedItems.length === 1 ? "" : "s"} selected.
        </p>
        {dialog === "authorized" && (
          <p>The employee will be asked to apply for leave in the Leave module.</p>
        )}
        {dialog === "unauthorized" && (
          <p>
            The day stays an absence and leave applications for it are blocked. You can change it to
            Authorized later.
          </p>
        )}
        <div className="form-group">
          <label>{dialog === "resolve-presence" ? "Reason (required)" : "Reason (optional)"}</label>
          <textarea
            className="form-control"
            rows={3}
            maxLength={500}
            value={form.reason}
            onChange={(event) => setForm({ ...form, reason: event.target.value })}
          />
        </div>
        {dialog === "resolve-presence" && (
          <>
            {employeeReason && (
              <p data-testid="resolve-employee-reason">
                <strong>Employee's reason:</strong> {employeeReason}
              </p>
            )}
            <p className="text-muted" style={{ fontSize: 12 }} data-testid="resolve-times-note">
              The day is recorded as present with the standard working hours (08:30 - 17:00).
            </p>
          </>
        )}
      </ActionDialog>

      <ActionDialog
        visible={history !== null}
        title={history ? `History - ${history.item.display_name}, ${formatDate(history.item.absence_date)}` : ""}
        width={760}
        confirmLabel="Close"
        onClose={() => setHistory(null)}
        onConfirm={() => setHistory(null)}
      >
        {history && history.rows.length === 0 && (
          <p className="text-muted">Nobody has acted on this day yet.</p>
        )}
        {history && history.rows.length > 0 && (
          <table className="table table-bordered table-condensed">
            <thead>
              <tr>
                <th>When</th>
                <th>Action</th>
                <th>Status</th>
                <th>By</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              {history.rows.map((row) => (
                <tr key={row.log_id}>
                  <td style={{ whiteSpace: "nowrap" }}>{row.action_date}</td>
                  <td>{describeLogAction(row.action_code)}</td>
                  <td>
                    {describeStatusCode(row.from_status)} {"->"} {describeStatusCode(row.to_status)}
                  </td>
                  <td>{row.actor_name}</td>
                  <td>
                    {row.comment}
                    {row.check_in ? ` (${formatTime(row.check_in)} - ${formatTime(row.check_out)})` : ""}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ActionDialog>

      <ActionDialog
        visible={balance !== null}
        title={balance ? `Leave balance - ${balance.item.display_name}` : ""}
        confirmLabel="Close"
        onClose={() => setBalance(null)}
        onConfirm={() => setBalance(null)}
      >
        <p className="text-muted">For information only. The system does not judge the balance.</p>
        {balance && (
          <table className="table table-bordered table-condensed">
            <thead>
              <tr>
                <th>Leave</th>
                <th>Taken</th>
                <th>Total</th>
                <th>Remaining</th>
              </tr>
            </thead>
            <tbody>
              {balance.rows.map((row) => (
                <tr key={row.leave_category_id}>
                  <td>{row.leave_category}</td>
                  <td>{row.taken}</td>
                  <td>{row.total}</td>
                  <td>{row.total - row.taken}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </ActionDialog>
    </div>
  );
}
